import React from 'react';
import type { SectorSentiment as SectorSentimentRow, SectorClassification } from '../../services/AlpacaService';
import styles from './SectorSentiment.module.css';

interface Props {
  sectors: SectorSentimentRow[];
  updatedAt: string | null;
  loading?: boolean;
}

function classificationLabel(c: SectorClassification): string {
  switch (c) {
    case 'HOT':
      return '🔥 Hot';
    case 'WARM':
      return 'Warm';
    case 'COOL':
      return 'Cool';
    case 'COLD':
      return '❄️ Cold';
    default:
      return 'Neutral';
  }
}

function classificationClass(c: SectorClassification): string {
  switch (c) {
    case 'HOT':
      return styles.badgeHot;
    case 'WARM':
      return styles.badgeWarm;
    case 'COOL':
      return styles.badgeCool;
    case 'COLD':
      return styles.badgeCold;
    default:
      return styles.badgeNeutral;
  }
}

function changeClass(pct: number): string {
  if (pct >= 1.0) return styles.changeStrongPos;
  if (pct > 0) return styles.changePos;
  if (pct <= -1.0) return styles.changeStrongNeg;
  if (pct < 0) return styles.changeNeg;
  return styles.changeFlat;
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const SectorSentiment: React.FC<Props> = ({ sectors, updatedAt, loading }) => {
  if (loading) {
    return (
      <div className={styles.emptyState}>
        <p>Loading sector sentiment...</p>
      </div>
    );
  }

  if (!sectors || sectors.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No sector sentiment yet</p>
        <p className={styles.emptyHint}>
          Snapshot is generated by the AI Strategist every 15 minutes during market hours.
        </p>
      </div>
    );
  }

  const hot = sectors.filter((s) => s.classification === 'HOT' || s.classification === 'WARM');
  const cold = sectors.filter((s) => s.classification === 'COOL' || s.classification === 'COLD');

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h3 className={styles.title}>Sector Sentiment</h3>
          <p className={styles.subtitle}>
            How the AI Strategist is seeing the tape right now. Leaders in hot sectors get boosted into the scanner.
          </p>
        </div>
        <div className={styles.updated}>Updated {timeAgo(updatedAt)}</div>
      </div>

      <div className={styles.summary}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>🔥 Hot / Warm</div>
          <div className={styles.summaryValue}>{hot.length}</div>
          <div className={styles.summarySymbols}>
            {hot
              .slice(0, 3)
              .map((s) => s.sector)
              .join(' • ') || '—'}
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>❄️ Cool / Cold</div>
          <div className={styles.summaryValue}>{cold.length}</div>
          <div className={styles.summarySymbols}>
            {cold
              .slice(0, 3)
              .map((s) => s.sector)
              .join(' • ') || '—'}
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Sectors tracked</div>
          <div className={styles.summaryValue}>{sectors.length}</div>
          <div className={styles.summarySymbols}>
            {sectors.reduce((acc, s) => acc + s.componentCount, 0)} component stocks
          </div>
        </div>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.colSector}>Sector</th>
              <th className={styles.colEtf}>ETF</th>
              <th className={styles.colCls}>Heat</th>
              <th className={styles.colNum}>1d %</th>
              <th className={styles.colNum}>5d %</th>
              <th className={styles.colNum}>Breadth</th>
              <th className={styles.colNum}>Vol</th>
              <th className={styles.colLeaders}>Top movers</th>
            </tr>
          </thead>
          <tbody>
            {sectors.map((s) => (
              <tr key={s.sector}>
                <td className={styles.sectorName}>{s.sector}</td>
                <td className={styles.etf}>{s.anchorEtf || '—'}</td>
                <td>
                  <span className={`${styles.badge} ${classificationClass(s.classification)}`}>
                    {classificationLabel(s.classification)}
                  </span>
                </td>
                <td className={`${styles.num} ${changeClass(s.change1dPct)}`}>
                  {s.change1dPct >= 0 ? '+' : ''}
                  {s.change1dPct.toFixed(2)}%
                </td>
                <td className={`${styles.num} ${changeClass(s.change5dPct)}`}>
                  {s.change5dPct >= 0 ? '+' : ''}
                  {s.change5dPct.toFixed(2)}%
                </td>
                <td className={styles.num}>{s.breadthPct.toFixed(0)}%</td>
                <td className={styles.num}>{s.volumeRatio > 0 ? `${s.volumeRatio.toFixed(1)}x` : '—'}</td>
                <td className={styles.leaders}>
                  {s.leaders.length === 0 ? (
                    <span className={styles.dim}>—</span>
                  ) : (
                    s.leaders.map((sym) => (
                      <span key={sym} className={styles.leaderChip}>
                        {sym}
                      </span>
                    ))
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className={styles.footnote}>
        <strong>How it works:</strong> each sector is scored from its anchor ETF's 1-day move, 5-day move, component
        breadth (% of stocks up today), and volume confirmation. HOT sectors must be +2%+ on 65%+ breadth and
        above-average volume. Leaders from HOT sectors are injected into the scanner as <code>SECTOR_LEADER</code>{' '}
        candidates so the strategist considers them even without a standard indicator trigger.
      </p>
    </div>
  );
};

export default SectorSentiment;
